import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface SupportedLanguage {
    code: string;
    name: string;
}

// Language names mapping - this matches what Crowdin/HirePort supports
const LANGUAGE_NAMES: Record<string, string> = {
    en: 'English',
    nl: 'Dutch',
    fr: 'French',
    de: 'German',
    es: 'Spanish',
    it: 'Italian',
};

/**
 * Hook to get supported languages from the i18n configuration
 * 
 * This is simple and reliable because:
 * - Languages are already defined in the i18n setup
 * - Translation files come from Crowdin 
 * - No need for API calls or dynamic loading
 * 
 * @returns {Object} { languages, languageCodes, isLoading, error }
 */
export const useSupportedLanguages = () => {
    const { i18n } = useTranslation();

    const { languages, languageCodes } = useMemo(() => {
        // Get languages directly from i18n configuration
        const availableLanguageCodes = Object.keys(i18n.store.data);

        const languages: SupportedLanguage[] = availableLanguageCodes.map(code => ({
            code,
            name: LANGUAGE_NAMES[code] || code.toUpperCase()
        }));

        return {
            languages,
            languageCodes: availableLanguageCodes,
        };
    }, [i18n.store.data]);

    return {
        languages,
        languageCodes,
        isLoading: false, // No async loading needed
        error: null,     // No API calls to fail
    };
};

/**
 * Simple hook that just returns the language codes
 * Useful for validation without needing the full language objects
 */
export const useSupportedLanguageCodes = (): string[] => {
    const { languageCodes } = useSupportedLanguages();
    return languageCodes;
};

export default useSupportedLanguages; 